(() => {
  if (customElements.get('rdb-sticky-purchase')) return;
  class RdbStickyPurchase extends HTMLElement {
    #controller;
    #observer;
    #visibilityObserver;
    #frame = 0;
    #variants = new Map();
    #forwarding = false;
    #choicesVisited = false;
    connectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#visibilityObserver?.disconnect();
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#choicesVisited = false;
      try {
        this.#variants = new Map(JSON.parse(this.querySelector('[data-sticky-variants]').textContent).map(variant => [String(variant.id), variant]));
      } catch { return; }
      this.addEventListener('click', this.#click, { signal });
      for (const type of ['scroll', 'resize', 'pageshow']) window.addEventListener(type, this.#schedule, { signal, passive: true });
      for (const type of ['change', 'input', 'focusin', 'focusout', 'variant:update', 'variant:selected', 'quantity-selector:update', 'rdb:currencychange', 'futureblink:currencychange']) document.addEventListener(type, this.#schedule, { signal });
      for (const type of ['click', 'change', 'input']) document.addEventListener(type, this.#visitChoices, { signal });
      window.visualViewport?.addEventListener('resize', this.#schedule, { signal });
      const information = document.querySelector('.product-information');
      if (information) {
        this.#observer = new MutationObserver(this.#schedule);
        this.#observer.observe(information, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-busy', 'aria-pressed', 'data-extra-quantity', 'class', 'value'] });
      }
      this.#visibilityObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.#schedule();
      });
      this.#visibilityObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'class', 'style'] });
      this.#visibilityObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      customElements.whenDefined('product-form-component').then(this.#schedule);
      this.#schedule();
    }
    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#visibilityObserver?.disconnect();
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
    #form() {
      return document.querySelector(`.product-information product-form-component[data-product-id="${this.dataset.productId}"] form[data-type="add-to-cart-form"]`);
    }
    #choiceTarget() {
      const picker = document.querySelector('.product-information variant-picker');
      const alternate = picker?.querySelector('input[type="radio"]:not(:checked):not(:disabled)[data-option-available="true"]');
      const select = [...(picker?.querySelectorAll('select') || [])].some(control => [...control.options].filter(option => !option.disabled).length > 1);
      if (alternate || select) return picker;
      const packaging = document.querySelector('.product-information rdb-packaging-options');
      return packaging?.querySelectorAll('input[type="radio"]:not(:disabled)').length > 1 ? packaging : null;
    }
    #schedule = () => {
      if (!this.isConnected || this.#frame) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = 0; this.#refresh(); });
    };
    #visitChoices = event => {
      if (!(event.target instanceof Element) || !event.target.closest('.product-information variant-picker, .product-information .rp-product-option, .product-information rdb-packaging-options, .product-information [name="quantity"]')) return;
      this.#choicesVisited = true;
      this.#schedule();
    };
    #refresh() {
      const form = this.#form();
      const native = form?.querySelector('[ref="addToCartButton"]');
      const variant = this.#variants.get(form?.querySelector('[name="id"]')?.value);
      if (!form || !native || !variant || !customElements.get('product-form-component')) { this.hidden = true; return; }
      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Number(quantityInput?.value || form.closest('product-form-component').dataset.quantityDefault || 1);
      const planId = form.querySelector('[name="selling_plan"]')?.value;
      const basePrice = planId ? variant.plans?.find(plan => String(plan.id) === String(planId))?.price : variant.price;
      const extras = form.querySelector('rdb-product-extras');
      const selected = [...(extras?.querySelectorAll('[data-extra-id][aria-pressed="true"]') || [])];
      const card = form.querySelector('[data-card-design]:checked');
      const cardPrice = card && card.dataset.cardDesign !== 'message' ? Number(card.dataset.cardPrice) : 0;
      const amount = basePrice * quantity + cardPrice + selected.reduce((total, item) => total + Number(item.dataset.extraPrice) * Number(item.dataset.extraQuantity || 1), 0);
      const validAmount = Number.isSafeInteger(amount) && amount >= 0 && Number.isInteger(quantity) && quantity > 0 && (!quantityInput || quantityInput.checkValidity());
      const display = window.rdbCurrencyPrices?.format(amount, this.dataset.currency) || `${this.dataset.currency} ${new Intl.NumberFormat(this.dataset.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount / 100)}`;
      const price = this.querySelector('[data-sticky-price]');
      price.hidden = !validAmount;
      if (validAmount && price.textContent !== display) price.textContent = display;
      const totalPanel = form.querySelector('[data-premium-total]');
      const inlinePrice = totalPanel?.querySelector('[data-premium-total-price]');
      if (totalPanel && inlinePrice) {
        const hideTotal = !validAmount || amount === basePrice;
        if (totalPanel.hidden !== hideTotal) totalPanel.hidden = hideTotal;
        if (validAmount && inlinePrice.textContent !== display) inlinePrice.textContent = display;
        if (validAmount && totalPanel.dataset.amount !== String(amount)) totalPanel.dataset.amount = String(amount);
      }
      const submit = this.querySelector('[data-sticky-submit]');
      const rect = native.getBoundingClientRect();
      const title = document.querySelector('.product-information h1')?.getBoundingClientRect();
      const passedInlineButton = rect.height > 0 && rect.bottom <= 0;
      const beforeInlineButton = rect.height > 0 && rect.top >= innerHeight - 70 && title && title.top < innerHeight;
      const choiceTarget = this.#choiceTarget();
      const chooseMode = !passedInlineButton && beforeInlineButton && Boolean(choiceTarget) && !this.#choicesVisited && !native.disabled;
      const mode = chooseMode ? 'choose' : 'purchase';
      if (this.dataset.mode !== mode) this.dataset.mode = mode;
      const label = chooseMode ? this.dataset.chooseLabel : native.querySelector('.add-to-cart-text__content')?.textContent?.trim() || native.textContent.trim();
      const output = this.querySelector('[data-sticky-label]');
      if (output.textContent !== label) output.textContent = label;
      submit.disabled = (chooseMode ? !choiceTarget : native.disabled) || form.getAttribute('aria-busy') === 'true' || !validAmount;
      this.setAttribute('aria-busy', String(form.getAttribute('aria-busy') === 'true'));
      this.querySelector('[data-sticky-extras]').hidden = !extras || chooseMode;
      const sourceError = form.querySelector('[ref="addToCartTextError"]');
      const error = this.querySelector('[data-sticky-error]');
      const errorText = sourceError && !sourceError.classList.contains('hidden') ? sourceError.textContent.trim() : '';
      error.hidden = !errorText;
      if (error.textContent !== errorText) error.textContent = errorText;
      if (errorText) submit.setAttribute('aria-describedby', error.id);
      else submit.removeAttribute('aria-describedby');
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '') && !['radio', 'checkbox', 'button', 'submit', 'hidden'].includes(document.activeElement?.type);
      const keyboard = window.visualViewport && innerHeight - window.visualViewport.height > 150;
      const consent = document.querySelector('#shopify-pc__banner, #shopify-pc__prefs');
      const consentVisible = consent && getComputedStyle(consent).display !== 'none' && consent.getBoundingClientRect().height > 0;
      this.hidden = !matchMedia('(max-width: 749px)').matches || !(passedInlineButton || beforeInlineButton) || Boolean(typing || keyboard || consentVisible);
    }
    #click = event => {
      const form = this.#form();
      if (event.target.closest('[data-sticky-extras]')) {
        const extras = form?.querySelector('rdb-product-extras');
        if (!extras) return;
        this.#choicesVisited = true;
        extras.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        extras.querySelector('[data-extra-category][aria-pressed="true"]')?.focus({ preventScroll: true });
        this.#schedule();
        return;
      }
      const action = event.target.closest('[data-sticky-submit]');
      const native = form?.querySelector('[ref="addToCartButton"]');
      if (!action || action.disabled || !native || native.disabled || this.#forwarding || form.getAttribute('aria-busy') === 'true') return;
      if (this.dataset.mode === 'choose') {
        const choices = this.#choiceTarget() || form;
        this.#choicesVisited = true;
        choices.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        const focus = choices.querySelector('input:checked:not(:disabled),select,summary,[ref="addToCartButton"]');
        focus?.focus({ preventScroll: true });
        this.#schedule();
        return;
      }
      this.#forwarding = true;
      native.click();
      this.#refresh();
      queueMicrotask(() => { this.#forwarding = false; this.#schedule(); });
    };
  }
  customElements.define('rdb-sticky-purchase', RdbStickyPurchase);
})();
